import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Card, Form, Input, Select, Button, Table, Typography, Space, Tag, DatePicker, Tooltip, Empty, Divider, message } from 'antd';
import { SendOutlined, HistoryOutlined, FileAddOutlined, SearchOutlined, ReloadOutlined, UserOutlined, CarOutlined, EnvironmentOutlined, PhoneOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { RootState } from '../../redux/store/store';
import { addAttachmentEnquiryRequest, resetAttachmentEnquiry, getAttachmentEnquiryRequest } from '../../redux/actions/attachmentEnquiryAction';
import { COPY_CELL } from "../../utils/copyGuard";
import { getEmployeeName } from "../../utils/currentUser";

const { Option } = Select;
const { Text } = Typography;
const { RangePicker } = DatePicker;

const PURPLE = 'rgb(136, 66, 153)';

const VEHICLE_TYPES = ['Tata Ace', 'Bolero', '407', 'Eicher', 'Driver'];

const formColStyle: React.CSSProperties = {
    flex: '0 0 38%',
    minWidth: 380,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
};
const tableColStyle: React.CSSProperties = {
    flex: '1 1 auto',
    minWidth: 0,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
};
const historyBodyStyle: React.CSSProperties = {
    flex: '1 1 auto',
    minHeight: 0,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    padding: '16px 20px',
};

const AddAttachmentenquiry = () => {
    const [form] = Form.useForm();
    const [messageApi, contextHolder] = message.useMessage();
    const dispatch = useDispatch();
    const { loading: submitLoading, success, error, list, getLoading, tablelength } = useSelector(
        (state: RootState) => state.attachmentEnquiry
    );
    const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
        dayjs(),
        dayjs(),
    ]);
    const [search, setSearch] = useState('');

    const vehicleType = Form.useWatch('Vehicle_Type', form);
    const isDriver = vehicleType === 'Driver';

    const fetchHistory = () => {
        dispatch(
            getAttachmentEnquiryRequest({
                cityid: '',
                fromdate: dateRange[0].format('YYYY-MM-DD'),
                todate: dateRange[1].format('YYYY-MM-DD'),
                type: '',
                status: '',
            })
        );
    };

    const lastFetchedRangeRef = useRef<string | null>(null);
    useEffect(() => {
        if (!dateRange?.[0] || !dateRange?.[1]) return;
        const rangeKey = `${dateRange[0].valueOf()}|${dateRange[1].valueOf()}`;
        if (lastFetchedRangeRef.current === rangeKey) return;
        lastFetchedRangeRef.current = rangeKey;
        fetchHistory();
    }, [dateRange]);

    const onFinish = (values: any) => {
        dispatch(addAttachmentEnquiryRequest({
            name: values.Name,
            number: values.Number,
            city: values.City,
            area: values.Area,
            address: values.Address ?? null,
            enquiry_type: values.Enquiry_Type,
            license: values.License ?? null,
            registration: values.Registration ?? null,
            model: values.Model ?? null,
            year: values.Make_Year ?? null,
            remarks: values.Remarks ?? null,
            attended_by: values.Attended_By ?? (getEmployeeName() || null),
            cityid: values.cityid ?? '',
        }));
    };

    useEffect(() => {
        if (success) {
            messageApi.success('Enquiry added successfully.');
            form.resetFields();
            dispatch(resetAttachmentEnquiry());
            fetchHistory();
        }
        if (error) {
            messageApi.error(typeof error === 'string' ? error : 'Failed to add enquiry.');
            dispatch(resetAttachmentEnquiry());
        }
    }, [success, error]);

    const rows = useMemo(() => {
        const q = search.trim().toLowerCase();
        const source = list || [];
        if (!q) return source;
        return source.filter((r: any) =>
            r.name?.toLowerCase().includes(q) ||
            r.number?.toLowerCase().includes(q) ||
            r.area?.toLowerCase().includes(q) ||
            r.city?.toLowerCase().includes(q) ||
            r.registration?.toLowerCase().includes(q) ||
            String(r.enquiry_id ?? '').toLowerCase().includes(q)
        );
    }, [search, list]);

    const label = (t: string) => <span style={{ fontWeight: 600, color: '#444' }}>{t}</span>;

    const historyColumns = [
        {
            title: 'ID',
            dataIndex: 'enquiry_id',
            key: 'enquiry_id',
            className: COPY_CELL,
            width: 75,
            align: 'center' as const,
            render: (text: string) => (
                <Text style={{ color: '#666', fontWeight: 600, fontFamily: 'ui-monospace, Menlo, monospace' }}>
                    {text}
                </Text>
            ),
        },
        {
            title: 'Date',
            dataIndex: 'created',
            key: 'date',
            width: 110,
            align: 'center' as const,
            render: (text: string) => <Text style={{ fontSize: 13 }}>{text ? dayjs(text).format('DD-MMM-YYYY') : '-'}</Text>,
        },
        {
            title: 'Time',
            dataIndex: 'created',
            key: 'time',
            width: 85,
            align: 'center' as const,
            render: (text: string) => (
                <Text style={{ fontVariantNumeric: 'tabular-nums', color: '#666', fontSize: 12 }}>
                    {text ? dayjs(text).format('hh:mm A') : '-'}
                </Text>
            ),
        },
        {
            title: 'Customer',
            dataIndex: 'name',
            key: 'name',
            className: COPY_CELL,
            width: 170,
            ellipsis: true,
            render: (text: string, record: any) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <span style={{
                        width: 28, height: 28, borderRadius: '50%', background: '#f3e8f6',
                        color: PURPLE, display: 'inline-flex', alignItems: 'center',
                        justifyContent: 'center', fontWeight: 700, fontSize: 12, flexShrink: 0,
                    }}>
                        {(text || '?').charAt(0).toUpperCase()}
                    </span>
                    <div style={{ lineHeight: 1.25, minWidth: 0, overflow: 'hidden' }}>
                        <div style={{ fontWeight: 600, color: '#333', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {text || '-'}
                        </div>
                        <div style={{ fontSize: 11, color: '#8c8c8c', fontVariantNumeric: 'tabular-nums' }}>
                            {record.number || '-'}
                        </div>
                    </div>
                </div>
            ),
        },
        {
            title: 'Type',
            dataIndex: 'enquiry_type',
            key: 'enquiry_type',
            width: 100,
            align: 'center' as const,
            filters: [
                { text: 'Call', value: 'Call' },
                { text: 'Walk-in', value: 'Walk-in' },
                { text: 'Reference', value: 'Reference' },
            ],
            onFilter: (value: any, record: any) => record.enquiry_type === value,
            render: (text: string) => {
                const map: any = {
                    'Call': { color: 'blue', icon: '📞' },
                    'Walk-in': { color: 'purple', icon: '🚶' },
                    'Reference': { color: 'green', icon: '🤝' },
                };
                const cfg = map[text] || { color: 'default', icon: '' };
                return (
                    <Tag color={cfg.color} style={{ borderRadius: 6, fontWeight: 500, padding: '1px 6px', margin: 0, fontSize: 12 }}>
                        {cfg.icon} {text}
                    </Tag>
                );
            },
        },
        {
            title: 'Location',
            dataIndex: 'area',
            key: 'area',
            width: 130,
            ellipsis: true,
            render: (text: string, record: any) => (
                <div style={{ lineHeight: 1.25, minWidth: 0 }}>
                    <div style={{ color: '#333', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {text || '-'}
                    </div>
                    <div style={{ fontSize: 11, color: '#8c8c8c' }}>{record.city || ''}</div>
                </div>
            ),
        },
        {
            title: 'Vehicle',
            dataIndex: 'model',
            key: 'model',
            width: 95,
            align: 'center' as const,
            ellipsis: true,
            render: (text: string) => <Text style={{ color: '#444', fontWeight: 500, fontSize: 12 }}>{text || '-'}</Text>,
        },
        {
            title: 'Registration',
            dataIndex: 'registration',
            key: 'registration',
            width: 125,
            align: 'center' as const,
            render: (text: string) => text ? (
                <Tag style={{
                    borderRadius: 6, fontWeight: 600, padding: '1px 6px', margin: 0,
                    fontFamily: 'ui-monospace, Menlo, monospace', letterSpacing: '0.5px', fontSize: 11,
                }}>
                    {text}
                </Tag>
            ) : '-',
        },
    ];

    return (
        <div className="fit-page fit-page--split attachment-add-page" style={{ width: '100%', maxWidth: '100%' }}>
            {contextHolder}
            <div className="att-layout">
                <div style={formColStyle}>
                    <Card
                        title={
                            <Space size={10} style={{ color: '#fff', padding: '4px 0' }}>
                                <FileAddOutlined style={{ fontSize: 20 }} />
                                <span style={{ fontSize: 18, fontWeight: 700, letterSpacing: '0.5px' }}>
                                    Add Enquiry
                                </span>
                            </Space>
                        }
                        variant="borderless"
                        styles={{
                            header: {
                                background: PURPLE,
                                borderTopLeftRadius: 16,
                                borderTopRightRadius: 16,
                                borderBottom: 'none',
                                padding: '16px 24px',
                            },
                            body: { padding: '20px 22px', background: '#fcfcff', borderRadius: '0 0 16px 16px' },
                        }}
                        className="fit-scroll-body"
                        style={{
                            borderRadius: 16,
                            border: '2px solid #86398f70',
                            boxShadow: '0 10px 40px rgba(137, 67, 154, 0.08)',
                            flex: '1 1 auto',
                            minHeight: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                        }}
                    >
                        <Form form={form} layout="vertical" onFinish={onFinish} requiredMark={false}>

                            <Divider plain style={{ margin: '0 0 14px' }}>
                                <span className="sec-label"><UserOutlined /> Customer</span>
                            </Divider>

                            <Row gutter={16}>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label={label('Number')}
                                        name="Number"
                                        getValueFromEvent={(e) => e.target.value.replace(/\D/g, '')}
                                        rules={[
                                            { required: true, message: 'Enter Number' },
                                            { pattern: /^\d{10}$/, message: 'Enter 10 digits' },
                                        ]}
                                    >
                                        <Input
                                            prefix={<PhoneOutlined style={{ color: '#bbb' }} />}
                                            placeholder="10-digit mobile"
                                            maxLength={10}
                                            inputMode="numeric"
                                            style={{ borderRadius: 8, height: 38 }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label={label('Name')}
                                        name="Name"
                                        getValueFromEvent={(e) =>
                                            e.target.value.replace(/[^A-Za-z\s]/g, '')
                                        }
                                        rules={[
                                            { required: true, message: 'Enter Name' },
                                        ]}
                                    >
                                        <Input
                                            placeholder="Full name"
                                            style={{ borderRadius: 8, height: 38 }}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label={label('City')}
                                        name="City"
                                        getValueFromEvent={(e) =>
                                            e.target.value.replace(/[^A-Za-z\s]/g, '')
                                        }
                                        rules={[
                                            { required: true, message: 'Enter City' },
                                        ]}
                                    >
                                        <Input
                                            prefix={<EnvironmentOutlined style={{ color: '#bbb' }} />}
                                            placeholder="City"
                                            style={{ borderRadius: 8, height: 38 }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item label={label('Address')} name="Area" rules={[{ required: true, message: 'Enter Address' }]}>
                                        <Input placeholder="Address" style={{ borderRadius: 8, height: 38 }} />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Divider plain style={{ margin: '4px 0 14px' }}>
                                <span className="sec-label"><CarOutlined /> Vehicle</span>
                            </Divider>

                            <Row gutter={16}>
                                <Col xs={24} sm={12}>
                                    <Form.Item label={label('Vehicle Type')} name="Vehicle_Type" rules={[{ required: true, message: 'Select Vehicle Type' }]}>
                                        <Select placeholder="Select Type" style={{ borderRadius: 8, height: 38 }}>
                                            {VEHICLE_TYPES.map((v) => (
                                                <Option key={v} value={v}>{v}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    {isDriver ? (
                                        <Form.Item
                                            label={label('License')}
                                            name="License"
                                            getValueFromEvent={(e) =>
                                                e.target.value.replace(/[^A-Za-z0-9]/g, '')
                                            }
                                            rules={[
                                                { required: true, message: 'Enter License' },
                                            ]}
                                        >
                                            <Input
                                                placeholder="License Number"
                                                style={{ borderRadius: 8, height: 38 }}
                                            />
                                        </Form.Item>
                                    ) : (
                                        <Form.Item
                                            label={label('Registration')}
                                            name="Registration"
                                            getValueFromEvent={(e) =>
                                                e.target.value
                                                    .replace(/[^A-Za-z0-9-]/g, '')
                                                    .toUpperCase()
                                            }
                                            rules={[
                                                { required: true, message: 'Enter Registration' },
                                            ]}
                                        >
                                            <Input
                                                placeholder="TN-00-XX-0000"
                                                style={{
                                                    borderRadius: 8,
                                                    height: 38,
                                                    textTransform: 'uppercase',
                                                }}
                                            />
                                        </Form.Item>
                                    )}
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col xs={24} sm={12}>
                                    <Form.Item label={label('Enquiry Type')} name="Enquiry_Type" rules={[{ required: true, message: 'Select Enquiry Type' }]}>
                                        <Select placeholder="Select Type" style={{ borderRadius: 8, height: 38 }}>
                                            <Option value="Call">Call 📞</Option>
                                            <Option value="Walk-in">Walk-in 🚶‍♂️</Option>
                                            <Option value="Reference">Reference 🤝</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>

                            {!isDriver && (
                                <Row gutter={16}>
                                    <Col xs={24} sm={12}>
                                        <Form.Item label={label('Model')} name="Model">
                                            <Input placeholder="Model" style={{ borderRadius: 8, height: 38 }} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item label={label('Make Year')} name="Make_Year">
                                            <Input
                                                placeholder="e.g. 2021"
                                                maxLength={4}
                                                inputMode="numeric"
                                                style={{ borderRadius: 8, height: 38 }}
                                                onChange={(e) => {
                                                    const value = e.target.value.replace(/[^0-9]/g, "");
                                                    form.setFieldValue("Make_Year", value);
                                                }}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            )}

                            <Divider plain style={{ margin: '4px 0 14px' }}>
                                <span className="sec-label">Notes</span>
                            </Divider>

                            <Row gutter={16}>
                                <Col xs={24} sm={12}>
                                    <Form.Item label={<span style={{ fontWeight: 600, color: '#444' }}>Remarks <em className="opt">optional</em></span>} name="Remarks">
                                        <Input placeholder="Remarks" style={{ borderRadius: 8, height: 38 }} />
                                    </Form.Item>
                                </Col>
                                {/* <Col span={12}>
                                    <Form.Item label={<span style={{ fontWeight: 600, color: '#444' }}>Follow <em className="opt">optional</em></span>} name="Follow">
                                        <Input placeholder="Follow up details" style={{ borderRadius: 8, height: 38 }} />
                                    </Form.Item>
                                </Col> */}
                            </Row>

                            <Form.Item style={{ marginBottom: 0, marginTop: 12 }}>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    icon={<SendOutlined />}
                                    block
                                    size="large"
                                    loading={submitLoading}
                                    style={{
                                        background: PURPLE,
                                        border: 'none',
                                        borderRadius: 10,
                                        fontWeight: 700,
                                        fontSize: 16,
                                        letterSpacing: '0.5px',
                                        boxShadow: '0 6px 16px rgba(137, 67, 154, 0.35)',
                                        height: 44,
                                    }}
                                >
                                    Add Attachment Enquiry
                                </Button>
                            </Form.Item>
                        </Form>
                    </Card>
                </div>

                <div style={tableColStyle}>
                    <Card
                        title={
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 12, flexWrap: 'wrap' }}>
                                <Space size={10} style={{ color: '#fff', padding: '4px 0' }}>
                                    <HistoryOutlined style={{ fontSize: 20 }} />
                                    <span style={{ fontSize: 18, fontWeight: 700, letterSpacing: '0.5px' }}>
                                        Enquiry History Log
                                    </span>
                                </Space>
                                <Tag style={{ borderRadius: 12, fontWeight: 600, padding: '2px 10px' }}>
                                    {rows.length} records
                                </Tag>
                            </div>
                        }
                        variant="borderless"
                        styles={{
                            header: {
                                background: PURPLE,
                                borderTopLeftRadius: 16,
                                borderTopRightRadius: 16,
                                borderBottom: '1px solid rgba(137, 67, 154, 0.1)',
                                padding: '16px 24px',
                            },
                            body: {
                                ...historyBodyStyle,
                                background: '#fff',
                                borderRadius: '0 0 16px 16px',
                                border: '2px solid #86398f70',
                            },
                        }}
                        className="fit-card"
                        style={{
                            borderRadius: 16,
                            boxShadow: '0 10px 40px rgba(137, 67, 154, 0.08)',
                            flex: '1 1 auto',
                            minHeight: 0,
                            minWidth: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                        }}
                    >
                        <div className="fit-table" style={{ flex: '1 1 auto', minHeight: 0, minWidth: 0, width: '100%' }}>
                            <Table
                                className="custom-black-grid"
                                columns={historyColumns}
                                //dataSource={rows}
                                rowKey="enquiry_id"
                                loading={getLoading}
                                scroll={{ x: 'max-content', y: 240 }}
                                locale={{
                                    emptyText: (
                                        <Empty
                                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                                            description={<Text type="secondary">No enquiries yet. Add one on the left.</Text>}
                                        />
                                    ),
                                }}
                                pagination={{
                                    pageSize: 10,
                                    pageSizeOptions: ['5', '10', '50', '100'],
                                    showSizeChanger: true,
                                    showTotal: (t, range) => `${range[0]}-${range[1]} of ${t} records`,
                                }}
                                style={{ marginBottom: 0 }}
                                rowHoverable
                            />
                        </div>
                    </Card>
                </div>
            </div>

            <style>
                {`
                .att-layout {
                    display: flex;
                    gap: 20px;
                    flex: 1 1 auto;
                    min-height: 0;
                    /* stretch, not flex-start: the columns have to inherit the
                       row's height for the cards to size against it. */
                    align-items: stretch;
                }
                @media (max-width: 1100px) {
                    .att-layout { flex-direction: column; align-items: stretch; }
                    .att-layout > * { flex: 0 0 auto !important; min-width: 0 !important; }
                }

                .custom-black-grid .ant-table-container {
                    border-top: 1px solid #d9d9d9 !important;
                    border-left: 1px solid #d9d9d9 !important;
                    border-radius: 0 !important;
                }
                .custom-black-grid .ant-table-thead > tr > th,
                .custom-black-grid .ant-table-tbody > tr > td {
                    border-bottom: 1px solid #d9d9d9 !important;
                    border-right: 1px solid #d9d9d9 !important;
                    border-radius: 0 !important;
                }
                .custom-black-grid .ant-table-thead > tr > th {
                    background: #86398f !important;
                    color: #fff !important;
                    font-size: 12px;
                    letter-spacing: 0.06em;
                    text-transform: uppercase;
                }
                .custom-black-grid .ant-table-thead > tr > th::before {
                    display: none !important;
                }
                .custom-black-grid .ant-table-column-sorter,
                .custom-black-grid .ant-table-filter-trigger {
                    color: rgba(255,255,255,0.6) !important;
                }
                .custom-black-grid .ant-table-column-sorter-up.active,
                .custom-black-grid .ant-table-column-sorter-down.active,
                .custom-black-grid .ant-table-filter-trigger.active {
                    color: #ffd666 !important;
                }
                .custom-black-grid .ant-table-tbody > tr:nth-child(even) > td {
                    background: #faf7fb;
                }
                .custom-black-grid .ant-table-tbody > tr:hover > td {
                    background: #f3e8f6 !important;
                }
                .custom-black-grid .ant-table-cell-fix-left {
                    background: inherit !important;
                }

                .custom-black-grid .ant-table-body,
                .custom-black-grid .ant-table-content {
                    scrollbar-width: thin !important;
                    scrollbar-color: rgba(136, 57, 143, 0.35) transparent !important;
                }
                .custom-black-grid .ant-table-body::-webkit-scrollbar,
                .custom-black-grid .ant-table-content::-webkit-scrollbar {
                    width: 6px !important;
                    height: 6px !important;
                    display: block !important;
                }
                .custom-black-grid .ant-table-body::-webkit-scrollbar-thumb,
                .custom-black-grid .ant-table-content::-webkit-scrollbar-thumb {
                    background: rgba(136, 57, 143, 0.35) !important;
                    border-radius: 6px !important;
                }

                .sec-label {
                    font-size: 11px;
                    font-weight: 700;
                    letter-spacing: 0.12em;
                    text-transform: uppercase;
                    color: ${PURPLE};
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                }
                .ant-divider-horizontal.ant-divider-with-text {
                    border-color: #ece3ef !important;
                }
                .opt {
                    font-style: normal;
                    font-weight: 400;
                    font-size: 11px;
                    color: #aaa;
                    margin-left: 4px;
                }

                .ant-form-vertical .ant-form-item {
                    margin-bottom: 12px !important;
                }
                .ant-form-vertical .ant-form-item-label {
                    padding-bottom: 2px !important;
                }

                /* Field pairs fall to one per row through their own xs={24}
                   spans, so no global .ant-col-12 override is needed here.
                   1100px, the same boundary .att-layout stacks on. */
                @media (max-width: 1100px) {
                    .attachment-add-page .ant-card-head { padding: 12px 16px !important; }
                    .attachment-add-page .ant-card-body { padding: 16px !important; }
                }
                `}
            </style>
        </div>
    );
};

export default AddAttachmentenquiry;